import type { PlayerSplitStatsResponse } from '@/api/generated/models'
import type { SplitStatLine } from '../types'
import { mapBattingLine, mapPitchingLine } from './statLines'

function partitionBySeason(
  rows: { splitLabel: string; line: SplitStatLine; year: number | undefined }[],
): { current: SplitStatLine[]; history: SplitStatLine[] } {
  if (!rows.length) return { current: [], history: [] }
  const years = rows.map(r => r.year).filter((y): y is number => y != null)
  // When no row carries a year (all null), treat everything as the current season.
  if (!years.length) {
    return { current: rows.map(({ line }) => ({ ...line, season: undefined })), history: [] }
  }
  const maxYear = Math.max(...years)
  const current: SplitStatLine[] = []
  const history: SplitStatLine[] = []
  for (const { line, year } of rows) {
    if (year === maxYear) {
      current.push({ ...line, season: undefined })
    } else {
      history.push({ ...line, season: year ?? null })
    }
  }
  return { current, history }
}

// One mapped row carries its split label, the stat line, and (for season rows)
// the year used to partition current-vs-history. Career rows have no year.
type Mapped = { splitLabel: string; line: SplitStatLine; year: number | undefined }

function mapSplitRows(
  rows: PlayerSplitStatsResponse['splitSeason'],
  isPitcher: boolean,
): Mapped[] {
  const mapped: Mapped[] = []
  for (const row of rows ?? []) {
    const splitLabel = row.split ?? ''
    const year = row.batting?.year ?? row.pitching?.year ?? undefined
    if (isPitcher && row.pitching) {
      const base = mapPitchingLine(row.pitching)
      mapped.push({ splitLabel, line: { ...base, split: splitLabel, label: undefined }, year })
    } else if (!isPitcher && row.batting) {
      const base = mapBattingLine(row.batting)
      mapped.push({ splitLabel, line: { ...base, split: splitLabel, label: undefined }, year })
    }
  }
  return mapped
}

function mapSituationalRows(rows: PlayerSplitStatsResponse['situationalSeason']): Mapped[] {
  const mapped: Mapped[] = []
  for (const row of rows ?? []) {
    const splitLabel = row.situation ?? ''
    const year = row.batting?.year ?? undefined
    if (row.batting) {
      const base = mapBattingLine(row.batting)
      mapped.push({ splitLabel, line: { ...base, split: splitLabel, label: undefined }, year })
    }
  }
  return mapped
}

// Career rows are aggregates (no year); flag them so the UI renders a "Career"
// total per split rather than a year-prefixed history row.
function asCareer(mapped: Mapped[]): SplitStatLine[] {
  return mapped.map(({ line }) => ({ ...line, season: undefined, careerTotal: true }))
}

export function mapSplits(api: PlayerSplitStatsResponse): { current: SplitStatLine[]; history: SplitStatLine[]; career: SplitStatLine[] } {
  const isPitcher = api.isPitcher ?? false
  const { current, history } = partitionBySeason(mapSplitRows(api.splitSeason, isPitcher))
  return { current, history, career: asCareer(mapSplitRows(api.splitCareer, isPitcher)) }
}

export function mapSituational(api: PlayerSplitStatsResponse): { current: SplitStatLine[]; history: SplitStatLine[]; career: SplitStatLine[] } {
  const { current, history } = partitionBySeason(mapSituationalRows(api.situationalSeason))
  return { current, history, career: asCareer(mapSituationalRows(api.situationalCareer)) }
}
